import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { 
  Settings, Building, Phone, Mail, Globe, Calculator, 
  Upload, Image, Save, Check, ChevronDown, ChevronRight
} from 'lucide-react';
import { invalidateGeolocationConfigCache } from '../utils/geolocation';
import { calculerTVAIntracommunautaire, validerInformationsSociete } from '../services/companyInfoService';

// Composant de section accordéon
const AccordionSection = ({ title, icon: Icon, isOpen, onToggle, children, badge }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-200">
    <button
      onClick={onToggle}
      className="w-full p-4 md:p-6 bg-white dark:bg-gray-800 border-none flex items-center justify-between cursor-pointer transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-left"
      aria-expanded={isOpen}
    >
      <div className="flex items-center gap-3 flex-1">
        <div className="p-2 bg-blue-50 dark:bg-blue-900 rounded-lg">
          <Icon className="text-blue-600 dark:text-blue-300" size={20} />
        </div>
        <div className="text-left">
          <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
          {badge && (
            <span className="inline-block px-2 py-1 text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 rounded-full">
              {badge}
            </span>
          )}
        </div>
      </div>
      {isOpen ? (
        <ChevronDown className="text-gray-400" size={20} />
      ) : (
        <ChevronRight className="text-gray-400" size={20} />
      )}
    </button>
    {isOpen && (
      <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 animate-fadeIn">
        {children}
      </div>
    )}
  </div>
);

// Composant de champ de formulaire
const Field = ({ label, children, help, error, className = '' }) => (
  <div className={`flex flex-col gap-2 ${className}`}>
    <label className="text-sm font-medium text-gray-700 dark:text-gray-200 leading-5">
      {label}
      {help && <span className="text-xs text-gray-500 dark:text-gray-400 font-normal ml-1">{help}</span>}
    </label>
    {children}
    {error && <span className="text-xs text-red-600 dark:text-red-400 font-medium">{error}</span>}
  </div>
);

// Composant de groupe de champs en grille
const FieldGroup = ({ children, columns = 1 }) => (
  <div className={`grid grid-cols-1 ${columns === 2 ? 'md:grid-cols-2' : ''} gap-4 md:gap-6`}>
    {children}
  </div>
);

const ResponsiveSettings = ({ db, onSettingsUpdate }) => {
  const [settings, setSettings] = useState({
    organizationName: '',
    timeZone: 'Europe/Paris',
    country: '',
    googleMapsApiKey: '',
    checkInRadius: 100,
    denominationSociale: '',
    sigle: '',
    formeJuridique: '',
    siren: '',
    siret: '',
    capitalSocial: '',
    siegeSocial: '',
    president: '',
    telephone: '',
    email: '',
    siteWeb: '',
    tvaIntracommunautaire: '',
    conditionsVente: '',
    mentionsLegales: '',
    logoUrl: '',
    useLogoInHeader: false,
    logoDisplayHeight: 32,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [openSections, setOpenSections] = useState({
    general: true,
    company: false,
    contact: false,
    accounting: false,
    appearance: false
  });

  useEffect(() => {
    const fetchSettings = async () => {
      const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSettings(data);
      }
    };
    fetchSettings();
  }, [db]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => {
      const newValue = type === 'checkbox' ? checked : value;
      const newSettings = {
        ...prev,
        [name]: newValue
      };

      if (name === 'siren' && value) {
        const tvaCalculee = calculerTVAIntracommunautaire(value);
        if (tvaCalculee && !prev.tvaIntracommunautaire) {
          newSettings.tvaIntracommunautaire = tvaCalculee;
        }
      }

      return newSettings;
    });
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const calculerTVA = () => {
    if (settings.siren) {
      const tvaCalculee = calculerTVAIntracommunautaire(settings.siren);
      setSettings(prev => ({
        ...prev,
        tvaIntracommunautaire: tvaCalculee
      }));
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner un fichier image valide (JPG, PNG, GIF, etc.)');
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        alert('Le fichier est trop volumineux. Taille maximale: 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setSettings(prev => ({
          ...prev,
          logoUrl: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setSettings(prev => ({
      ...prev,
      logoUrl: ''
    }));
  };

  const validerInformations = () => {
    const result = validerInformationsSociete(settings);
    setValidationResult(result);
    return result;
  };

  const saveSettings = async () => {
    const validation = validerInformations();
    
    if (!validation) {
      setValidationResult({ valide: false, erreurs: ['Erreur de validation'], avertissements: [] });
      return;
    }
    if (!validation.valide) {
      setValidationResult(validation);
      return;
    }

    setIsSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'general'), settings);
      setSaveSuccess(true);
      onSettingsUpdate(settings);
      invalidateGeolocationConfigCache();
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde des paramètres:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-0">
      {/* En-tête */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">Paramètres</h1>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">Configuration de votre application</p>
            </div>
            <div className="flex gap-3 items-center">
              {saveSuccess && (
                <div className="flex items-center gap-2 px-3 py-2 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200 border border-green-200 dark:border-green-700 rounded-lg text-sm font-medium">
                  <Check size={16} />
                  Sauvegardé
                </div>
              )}
              <button
                onClick={saveSettings}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors duration-200"
              >
                <Save size={16} />
                {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">
        <div className="flex flex-col gap-4 md:gap-6 lg:gap-8">
          {/* Section Générale */}
          <AccordionSection
            title="Configuration générale"
            icon={Settings}
            isOpen={openSections.general}
            onToggle={() => toggleSection('general')}
            badge="Essentiel"
          >
            <FieldGroup columns={2}>
              <Field label="Nom de l'organisation">
                <input
                  type="text"
                  name="organizationName"
                  value={settings.organizationName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Entrez le nom de votre organisation"
                />
              </Field>

              <Field label="Fuseau horaire">
                <select
                  name="timeZone"
                  value={settings.timeZone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Europe/Paris">Europe/Paris (France)</option>
                  <option value="Europe/Brussels">Europe/Brussels (Belgique)</option>
                  <option value="Europe/Zurich">Europe/Zurich (Suisse)</option>
                  <option value="Europe/Luxembourg">Europe/Luxembourg (Luxembourg)</option>
                </select>
              </Field>

              <Field label="Pays">
                <input
                  type="text"
                  name="country"
                  value={settings.country}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Entrez le pays principal d'activité"
                />
              </Field>

              <Field 
                label="Rayon de pointage GPS (mètres)"
                help="Distance maximale autorisée pour le pointage GPS"
              >
                <input
                  type="number"
                  name="checkInRadius"
                  value={settings.checkInRadius}
                  onChange={handleInputChange}
                  min="10"
                  max="1000"
                  step="10"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </Field>
            </FieldGroup>
          </AccordionSection>

          {/* Section Société */}
          <AccordionSection
            title="Informations de la société"
            icon={Building}
            isOpen={openSections.company}
            onToggle={() => toggleSection('company')}
          >
            <FieldGroup columns={2}>
              <Field label="Dénomination sociale">
                <input
                  type="text"
                  name="denominationSociale"
                  value={settings.denominationSociale}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="OMAD SERVICES"
                />
              </Field>

              <Field label="Sigle">
                <input
                  type="text"
                  name="sigle"
                  value={settings.sigle}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="OMAD"
                />
              </Field>

              <Field label="Forme juridique">
                <input
                  type="text"
                  name="formeJuridique"
                  value={settings.formeJuridique}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="SAS (Société par Actions Simplifiée)"
                />
              </Field>

              <Field label="Capital social">
                <input
                  type="text"
                  name="capitalSocial"
                  value={settings.capitalSocial}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="500 €"
                />
              </Field>

              <Field label="SIREN">
                <input
                  type="text"
                  name="siren"
                  value={settings.siren}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="922 592 662"
                />
              </Field>

              <Field label="SIRET">
                <input
                  type="text"
                  name="siret"
                  value={settings.siret}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="922 592 662 00014"
                />
              </Field>

              <Field label="Président(e)" className="md:col-span-2">
                <input
                  type="text"
                  name="president"
                  value={settings.president}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Mme Virginie Cahagne"
                />
              </Field>

              <Field label="Siège social" className="md:col-span-2">
                <textarea
                  name="siegeSocial"
                  value={settings.siegeSocial}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical min-h-[80px]"
                  placeholder="30 bis Rue des Bondoires, 18230 Saint-Doulchard"
                />
              </Field>
            </FieldGroup>
          </AccordionSection>

          {/* Section Contact */}
          <AccordionSection
            title="Coordonnées de contact"
            icon={Phone}
            isOpen={openSections.contact}
            onToggle={() => toggleSection('contact')}
          >
            <FieldGroup columns={2}>
              <Field label="Téléphone">
                <input
                  type="tel"
                  name="telephone"
                  value={settings.telephone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="06 67 72 73 30"
                />
              </Field>

              <Field label="Email principal">
                <input
                  type="email"
                  name="email"
                  value={settings.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="contact@omadservices.com"
                />
              </Field>

              <Field label="Site web">
                <input
                  type="url"
                  name="siteWeb"
                  value={settings.siteWeb}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="www.omadservices.com"
                />
              </Field>
            </FieldGroup>
          </AccordionSection>

          {/* Section Comptabilité */}
          <AccordionSection
            title="Configuration comptable"
            icon={Calculator}
            isOpen={openSections.accounting}
            onToggle={() => toggleSection('accounting')}
          >
            <div className="space-y-4">
              <Field 
                label="Numéro de TVA intracommunautaire"
                help="Format: FR + SIREN (calculé automatiquement si vide)"
              >
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="tvaIntracommunautaire"
                    value={settings.tvaIntracommunautaire}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="FRXX 922 592 662"
                  />
                  <button
                    type="button"
                    onClick={calculerTVA}
                    disabled={!settings.siren}
                    className="flex items-center justify-center px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200 min-w-[44px] min-h-[44px]"
                    title="Calculer automatiquement la TVA à partir du SIREN"
                  >
                    <Calculator size={16} />
                  </button>
                </div>
              </Field>

              <Field label="Conditions générales de vente">
                <textarea
                  name="conditionsVente"
                  value={settings.conditionsVente}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical min-h-[80px]"
                  placeholder="Entrez vos conditions générales de vente..."
                />
              </Field>

              <Field label="Mentions légales">
                <textarea
                  name="mentionsLegales"
                  value={settings.mentionsLegales}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical min-h-[80px]"
                  placeholder="Entrez vos mentions légales..."
                />
              </Field>
            </div>
          </AccordionSection>

          {/* Section Apparence */}
          <AccordionSection
            title="Apparence"
            icon={Image}
            isOpen={openSections.appearance}
            onToggle={() => toggleSection('appearance')}
          >
            <div className="space-y-4">
              <Field label="Logo de l'entreprise">
                <div className="space-y-4">
                  {settings.logoUrl && (
                    <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <img 
                        src={settings.logoUrl} 
                        alt="Logo de l'entreprise" 
                        className="w-16 h-16 object-contain border border-gray-200 dark:border-gray-600 rounded"
                      />
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 dark:text-gray-300">Aperçu du logo</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Le logo apparaîtra sur tous les documents</p>
                      </div>
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors duration-200"
                      >
                        Supprimer
                      </button>
                    </div>
                  )}
                  
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-blue-400 dark:hover:border-blue-500 transition-colors duration-200">
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <label 
                      htmlFor="logo-upload"
                      className="cursor-pointer block"
                    >
                      <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                      <p className="text-gray-600 dark:text-gray-200 font-medium">
                        {settings.logoUrl ? 'Changer le logo' : 'Ajouter un logo'}
                      </p>
                      <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                        PNG, JPG, GIF - Max 2MB
                      </p>
                    </label>
                  </div>
                </div>
              </Field>

              <Field label="Utilisation du logo">
                <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="useLogoInHeader"
                      name="useLogoInHeader"
                      checked={settings.useLogoInHeader}
                      onChange={(e) => setSettings(prev => ({ ...prev, useLogoInHeader: e.target.checked }))}
                      className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <label htmlFor="useLogoInHeader" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Utiliser le logo dans l'en-tête de l'application
                    </label>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-300 mt-2 ml-7">
                    {settings.useLogoInHeader 
                      ? "Le logo remplacera le nom de l'organisation dans l'en-tête" 
                      : "Le nom de l'organisation sera affiché dans l'en-tête"}
                  </p>
                </div>
              </Field>
            </div>
          </AccordionSection>
        </div>
      </div>
    </div>
  );
};

export default ResponsiveSettings;
